import {
  Action,
  config,
  defaultProcessingSaga,
  defaultResponseProcessing,
  fetchAuthSaga,
  FetchResponse,
} from '@triare/auth-redux';
import {
  put, delay, takeLatest, call,
} from 'redux-saga/effects';
import { actions, Post, PostStatus } from './index';
import { PaginatedResponse, PaginationProps } from '../types';
import { Role } from '../../enums/user';
import { PostEntity } from './list';

export type PostChildrenListData = PaginatedResponse<Post>

export type PostChildrenListResponse = PostChildrenListData

export type PostOrderColumn = 'id' | 'text' | 'likeNumber' | 'dislikeNumber' | 'subPostCount' | 'createdAt' | 'updatedAt';

export type PostChildrenListParams = PaginationProps<PostOrderColumn> & {
  id?: string;
  status?: PostStatus;
  entity?: PostEntity;
  role?: Role;
  updatedAtStart?: string;
  updatedAtEnd?: string;
  search?: string;
  business?: string;
  user?: string;
}

export interface PostChildrenListPayload {
  parentId: string;
  params: PostChildrenListParams;
}

export const postChildrenList = (payload: PostChildrenListPayload): Action<PostChildrenListPayload> => ({
  type: actions.postChildrenListTrigger.toString(),
  payload,
});

export const postChildrenListClear = (): Action => ({
  type: actions.postChildrenListClear.toString(),
});

export const postChildrenListErrorClear = (): Action => ({
  type: actions.postChildrenListErrorReset.toString(),
});

export function* postChildrenListRequestSaga({ payload }: Action<PostChildrenListParams>) {
  yield put({
    type: actions.postChildrenListStart.toString(),
  });

  if (!payload) {
    yield put({
      type: actions.postChildrenListError.toString(),
      payload: {
        message: 'PostChildrenList: Parameters not passed.',
      },
    });

    return;
  }

  const { parentId, ...restParams } = payload

  const params = new URLSearchParams(Object.entries(restParams).filter((entry) => !!entry[1]));

  const response = (yield call(
    fetchAuthSaga,
    `${process.env.REACT_APP_API}posts/${parentId}/children?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', },
    },
    {},
    config.fetchDelay,
  )) as FetchResponse;

  yield defaultResponseProcessing<PostChildrenListData, PostChildrenListResponse>(
    response,
    actions.postChildrenListSuccess.toString(),
    actions.postChildrenListError.toString(),
    false,
    (postChildrenListData) => postChildrenListData,
  );
}

export default function* saga() {
  yield takeLatest(
    actions.postChildrenListTrigger.toString(),
    defaultProcessingSaga(
      postChildrenListRequestSaga,
      {
        error: actions.postChildrenListError.toString(),
        locationCancel: actions.postChildrenListClear.toString(),
      },
    ),
  );

  yield takeLatest(actions.postChildrenListSuccess.toString(), function* clear() {
    if (config.delaySuccess) {
      yield delay(config.delaySuccess);
    }
  });

  yield takeLatest(actions.postChildrenListError.toString(), function* errorReset() {
    if (config.delayError) {
      yield delay(config.delayError);
    }

    yield put({
      type: actions.postChildrenListErrorReset.toString(),
    });
  });
}
